import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../App';

const FROM_OPTIONS = [
  { key: 'auto',     label: 'Auto (by store)' },
  { key: 'euphoric', label: 'Euphoric Operations' },
  { key: 'tnt',      label: 'TNT' },
  { key: 'fba',      label: 'FBA Firestarter' },
];

// Services offered by the new provider's bulk template (their sample file's values).
const V2_SERVICES = [
  { key: 'usps_ground_advantage', label: 'USPS Ground Advantage' },
  { key: 'usps_priority',         label: 'USPS Priority' },
];

// Home inventory = bin/location starting with OC or WC (physical location field wins
// over the SKU's bin prefix, e.g. bin UNIFI but location WC3 is actually at home).
const locOf = (o) => String(o.location || o.sku_bin || '').trim();
const isHome = (o) => /^(OC|WC)/i.test(locOf(o));

export default function Labels() {
  const [queue, setQueue]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [from, setFrom]       = useState('auto');
  const [selected, setSelected] = useState({});
  const [weights, setWeights]   = useState({});
  const [pasteFor, setPasteFor] = useState(null);   // order id whose address box is open
  const [pasteText, setPasteText] = useState('');
  const [pasteBusy, setPasteBusy] = useState(false);
  async function savePastedAddress(id) {
    setPasteBusy(true);
    try {
      await api.patch(`/labels/address/${id}`, { text: pasteText });
      setPasteFor(null); setPasteText('');
      await load();
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally { setPasteBusy(false); }
  }
  const [showUpload, setShowUpload] = useState(false);
  const [trackText, setTrackText]   = useState('');
  const [matchRes, setMatchRes]     = useState(null);
  const [picks, setPicks]           = useState({});
  const [applying, setApplying]     = useState(false);
  const [applyRes, setApplyRes]     = useState(null);
  const [locFilter, setLocFilter]   = useState('all');   // all | home | warehouse
  const [sortField, setSortField]   = useState(null);    // null | 'loc' | 'ordered' | 'item'
  const [sortDir, setSortDir]       = useState('asc');   // asc | desc
  const [v2Service, setV2Service]   = useState(V2_SERVICES[0].key); // new-provider CSV only

  const load = useCallback(() => {
    setLoading(true);
    api.get('/labels/queue').then(r => {
      const q = r.data.queue || [];
      setQueue(q);
      const w = {}; q.forEach(o => { if (o.weight != null) w[o.id] = o.weight; });
      setWeights(w);
      setSelected({});
      setLoading(false);
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  // Filtered + optionally location-sorted view of the queue
  let view = queue;
  if (locFilter === 'home') view = view.filter(isHome);
  else if (locFilter === 'warehouse') view = view.filter(o => !isHome(o));
  const tsOf = (o) => { const t = Date.parse(o.ordered_at); return isNaN(t) ? 0 : t; };
  if (sortField) {
    view = [...view].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'loc')          cmp = locOf(a).toUpperCase().localeCompare(locOf(b).toUpperCase());
      else if (sortField === 'item')    cmp = String(a.sku || '').toUpperCase().localeCompare(String(b.sku || '').toUpperCase());
      else if (sortField === 'ordered') cmp = tsOf(a) - tsOf(b);
      if (sortDir === 'desc') cmp = -cmp;
      if (cmp !== 0) return cmp;
      return tsOf(a) - tsOf(b);   // tiebreak: oldest order first (FIFO)
    });
  }
  const homeCount = queue.filter(isHome).length;
  const whCount   = queue.length - homeCount;

  function saveWeight(id, val) {
    api.patch(`/labels/weight/${id}`, { weight: val === '' ? null : parseFloat(val) }).catch(() => {});
  }
  function toggle(id) { setSelected(p => ({ ...p, [id]: !p[id] })); }
  function toggleAll() {
    const all = view.length > 0 && view.every(o => selected[o.id]);
    setSelected(p => { const next = { ...p }; view.forEach(o => { next[o.id] = !all; }); return next; });
  }
  function handleSort(field) {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  }
  const arrow = (field) => sortField === field ? (sortDir === 'asc' ? '▲' : '▼') : '⇅';
  const fmtOrdered = (s) => { if (!s) return ''; const d = new Date(s); return isNaN(d) ? '' : d.toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' }); };

  const hasW = (id) => weights[id] != null && weights[id] !== '';
  const selectedIds = queue.filter(o => selected[o.id]).map(o => o.id);
  const weighedCount = queue.filter(o => hasW(o.id)).length;
  const allViewChecked = view.length > 0 && view.every(o => selected[o.id]);
  const exportCountLabel = selectedIds.length ? ` (${selectedIds.length})` : weighedCount ? ` (${weighedCount} weighed)` : '';

  function exportCSV(format) {
    const ids = (selectedIds.length ? selectedIds : queue.filter(o => hasW(o.id)).map(o => o.id));
    if (!ids.length) return;
    // Both providers reject a row with no weight — catch it here instead of at upload.
    const noWeight = queue.filter(o => ids.includes(o.id) && !hasW(o.id));
    if (noWeight.length) {
      window.alert(
        `${noWeight.length} selected order(s) have no weight yet — the label provider rejects those rows.\n\n` +
        noWeight.slice(0, 12).map(o => `• ${o.order_number} — ${o.to?.name || ''}`).join('\n') +
        (noWeight.length > 12 ? `\n…and ${noWeight.length - 12} more` : '') +
        `\n\nEnter a weight for each, then export.`
      );
      return;
    }
    const extra = format === 'v2' ? `&format=v2&service=${v2Service}` : '';
    const a = document.createElement('a');
    a.href = `/api/labels/export?from=${from}&ids=${ids.join(',')}${extra}`;
    a.download = '';
    document.body.appendChild(a); a.click(); a.remove();
  }
  async function markShipped() {
    if (!selectedIds.length) return;
    if (!window.confirm(`Mark ${selectedIds.length} order(s) as labeled? They'll leave the queue.`)) return;
    await api.post('/labels/mark-shipped', { ids: selectedIds });
    load();
  }

  async function doMatch() {
    setApplyRes(null);
    const r = await api.post('/labels/tracking/match', { text: trackText });
    setMatchRes(r.data.results);
    const p = {};
    r.data.results.forEach((res, i) => { if (res.status === 'matched') p[i] = res.candidates[0].id; });
    setPicks(p);
  }
  function readFile(e) {
    const f = e.target.files[0]; if (!f) return;
    const rd = new FileReader(); rd.onload = () => setTrackText(rd.result); rd.readAsText(f);
  }
  async function doApply() {
    const items = (matchRes || []).map((r, i) => picks[i] ? { order_id: Number(picks[i]), tracking: r.tracking, carrier: r.carrier } : null).filter(Boolean);
    if (!items.length) return;
    if (!window.confirm(`Push tracking to ${items.length} order(s)? This marks them shipped on the marketplace and emails buyers their tracking.`)) return;
    setApplying(true);
    const r = await api.post('/labels/tracking/apply', { items, dryRun: false });
    setApplyRes(r.data.results); setApplying(false); load();
  }

  if (loading) return <div className="loading">Loading label queue…</div>;

  const chip = (key, label, count) => (
    <button key={key} className="btn btn-ghost" onClick={() => setLocFilter(key)}
      style={{ padding: '6px 11px', fontSize: 12, fontWeight: locFilter === key ? 700 : 400,
        borderColor: locFilter === key ? 'var(--accent)' : 'var(--border)',
        color: locFilter === key ? 'var(--accent)' : 'var(--muted)' }}>
      {label} <span style={{ opacity: .7 }}>{count}</span>
    </button>
  );

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Labels</div>
        <button className="btn btn-ghost" onClick={load}>Refresh</button>
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 13, color: 'var(--muted)' }}>Ship from</label>
        <select value={from} onChange={e => setFrom(e.target.value)}
          style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: 14 }}>
          {FROM_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {chip('all', 'All', queue.length)}
          {chip('home', '🏠 Home', homeCount)}
          {chip('warehouse', '📦 Warehouse', whCount)}
        </div>
        <span style={{ fontSize: 12, color: 'var(--muted)' }}>{weighedCount} weighed</span>
        <div style={{ flex: 1 }} />
        <button className="btn btn-primary" disabled={!selectedIds.length && weighedCount === 0} onClick={() => exportCSV('legacy')}
          title="Original label provider's CSV layout (19 columns)">
          ⬇ Export CSV{exportCountLabel}
        </button>
        <select value={v2Service} onChange={e => setV2Service(e.target.value)}
          title="Shipping service written into the new provider's CSV"
          style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: 13 }}>
          {V2_SERVICES.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
        <button className="btn btn-primary" disabled={!selectedIds.length && weighedCount === 0} onClick={() => exportCSV('v2')}
          title="New label provider's bulk template (27 columns, includes carrier/service)">
          ⬇ Export CSV — new provider{exportCountLabel}
        </button>
        <button className="btn btn-ghost" disabled={!selectedIds.length} onClick={markShipped}>Mark shipped</button>
        <button className="btn btn-ghost" onClick={() => { setShowUpload(true); setMatchRes(null); setApplyRes(null); }}>⬆ Upload Tracking</button>
      </div>

      {queue.length === 0 ? (
        <div className="empty-state"><div>No unshipped orders in the queue 🎉</div></div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead><tr>
              <th><input type="checkbox" checked={allViewChecked} onChange={toggleAll} /></th>
              <th>Img</th><th>Order #</th>
              <th onClick={() => handleSort('loc')} style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                title="Sort by location">Loc {arrow('loc')}</th>
              <th>Ch</th><th>From</th><th>Ship To</th><th>Address</th>
              <th onClick={() => handleSort('item')} style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                title="Sort by item — groups same SKUs, oldest order first">Item {arrow('item')}</th>
              <th>Qty</th><th>Price</th>
              <th onClick={() => handleSort('ordered')} style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                title="Sort by order date/time (oldest → newest)">Ordered {arrow('ordered')}</th>
              <th>Ship By</th><th>Weight (lb)</th>
            </tr></thead>
            <tbody>
              {view.length === 0 ? (
                <tr><td colSpan={14} style={{ textAlign: 'center', color: 'var(--muted)', padding: 20 }}>
                  No {locFilter === 'home' ? '🏠 home' : '📦 warehouse'} orders in the queue.
                </td></tr>
              ) : view.map(o => {
                const t = o.to || {};
                const addr = [t.street, t.street2, `${t.city || ''} ${t.state || ''} ${t.zip || ''}`.trim()].filter(Boolean).join(', ');
                const shipBy = o.ship_by ? new Date(o.ship_by).toLocaleDateString() : '';
                const home = isHome(o);
                return (
                  <tr key={o.id} style={{ background: selected[o.id] ? 'rgba(108,99,255,.06)' : '' }}>
                    <td><input type="checkbox" checked={!!selected[o.id]} onChange={() => toggle(o.id)} /></td>
                    <td>{o.image
                      ? <img src={o.image} alt="" style={{ width: 46, height: 46, objectFit: 'cover', borderRadius: 6, background: 'var(--surface2)' }} onError={e => { e.target.style.visibility = 'hidden'; }} />
                      : <div style={{ width: 46, height: 46, borderRadius: 6, background: 'var(--surface2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>📦</div>}</td>
                    <td className="mono" style={{ fontSize: 12 }}>{o.order_number}</td>
                    <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                      <span title={home ? 'Home inventory' : 'Warehouse / remote'}>{home ? '🏠' : '📦'}</span>{' '}
                      <span className="mono" style={{ fontWeight: 700, color: home ? 'var(--accent2)' : 'var(--muted)' }}>{locOf(o) || '—'}</span>
                    </td>
                    <td style={{ fontSize: 11 }}>{(o.channel || '').replace('ebay_', '')}</td>
                    <td style={{ fontSize: 11, color: 'var(--accent2)' }}>{from === 'auto' ? (o.from_label || '—') : (FROM_OPTIONS.find(f => f.key === from)?.label || '')}</td>
                    <td style={{ fontSize: 13, color: t.name ? '' : 'var(--danger)' }}>{t.name || (o.ship_via === 'seller_central' ? '—' : '⚠ no name')}</td>
                    <td style={{ fontSize: 12, color: 'var(--muted)', maxWidth: 250 }}>
                      {o.needs_address ? (
                        pasteFor === o.id ? (
                          <div>
                            <textarea autoFocus rows={5} value={pasteText} onChange={e => setPasteText(e.target.value)}
                              placeholder={(o.channel === 'amazon' ? 'Paste the Seller Central "Ship to" block:' : 'Paste the ship-to address from the order page:') + '\nName\nStreet\nCity, ST 12345'}
                              style={{ width: 240, fontSize: 12, fontFamily: 'inherit' }} />
                            <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                              <button className="btn btn-primary" disabled={pasteBusy || !pasteText.trim()} onClick={() => savePastedAddress(o.id)}>{pasteBusy ? '…' : 'Save'}</button>
                              <button className="btn btn-ghost" onClick={() => { setPasteFor(null); setPasteText(''); }}>Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <span>
                            <span style={{ color: '#ff9900' }} title={o.channel === 'amazon' ? "Amazon doesn't share the street address with the API — copy the Ship-to block from the Seller Central order page." : 'No order API for this channel — copy the ship-to address from the order page.'}>📦 {addr || (o.channel === 'amazon' ? 'address not shared by Amazon' : 'no address yet')}</span>
                            {' '}<button className="btn btn-ghost" style={{ fontSize: 11, padding: '1px 6px' }} onClick={() => { setPasteFor(o.id); setPasteText(''); }}>Paste address</button>
                          </span>
                        )
                      ) : (addr || '⚠ missing')}
                    </td>
                    <td style={{ fontSize: 11 }}><div className="mono">{o.sku}</div><div style={{ color: 'var(--muted)' }}>{(o.title || '').slice(0, 34)}</div></td>
                    <td style={{ textAlign: 'center' }}><span style={{ fontSize: 13, fontWeight: 700, padding: '2px 9px', borderRadius: 10, background: o.qty > 1 ? 'rgba(255,170,60,.18)' : 'rgba(136,136,144,.12)', color: o.qty > 1 ? '#ffaa3c' : 'var(--muted)' }}>×{o.qty}</span></td>
                    <td style={{ fontSize: 13, fontWeight: 700, color: (o.price >= 50 ? 'var(--accent2)' : 'var(--text)') }}>{o.price != null ? `$${Number(o.price).toFixed(2)}` : '—'}</td>
                    <td style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{fmtOrdered(o.ordered_at)}</td>
                    <td style={{ fontSize: 11 }}>{shipBy}</td>
                    <td>
                      <input type="number" step="0.1" min="0" placeholder="—"
                        value={weights[o.id] ?? ''}
                        onChange={e => setWeights(p => ({ ...p, [o.id]: e.target.value }))}
                        onBlur={e => saveWeight(o.id, e.target.value)}
                        style={{ width: 70, padding: '5px 7px', borderRadius: 6, border: `1px solid ${hasW(o.id) ? 'var(--accent2)' : 'var(--border)'}`, background: 'var(--surface)', color: 'var(--text)', fontFamily: 'monospace', textAlign: 'center' }} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showUpload && (
        <div onClick={() => setShowUpload(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflow: 'auto', padding: '40px 16px' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, width: 'min(840px,100%)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontSize: 16, fontWeight: 700 }}>Upload Tracking</div>
              <button className="btn btn-ghost" onClick={() => setShowUpload(false)}>✕</button>
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>Paste lines as <code>NAME : TRACKING</code> (or load a .txt). Matched against the current queue — you confirm before anything is pushed to the stores.</div>
            <textarea value={trackText} onChange={e => setTrackText(e.target.value)} rows={6} placeholder={'ALYSSIA SHERIFF : 9302083628300956226334'}
              style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontFamily: 'monospace', fontSize: 12, boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', margin: '10px 0' }}>
              <input type="file" accept=".txt,text/plain" onChange={readFile} style={{ fontSize: 12 }} />
              <div style={{ flex: 1 }} />
              <button className="btn btn-primary" disabled={!trackText.trim()} onClick={doMatch}>Match</button>
            </div>
            {matchRes && !applyRes && (
              <>
                <div className="table-wrap" style={{ marginTop: 4 }}>
                  <table><thead><tr><th>Name</th><th>Tracking</th><th>Carrier</th><th>Status</th><th>Order</th></tr></thead>
                    <tbody>{matchRes.map((r, i) => (
                      <tr key={i}>
                        <td style={{ fontSize: 12 }}>{r.name}</td>
                        <td className="mono" style={{ fontSize: 11 }}>{r.tracking || '—'}</td>
                        <td style={{ fontSize: 11 }}>{r.carrier}</td>
                        <td><span style={{ fontSize: 11, fontWeight: 700, color: r.status === 'matched' ? 'var(--accent2)' : r.status === 'ambiguous' ? '#ffaa3c' : 'var(--danger)' }}>{r.status === 'matched' ? '✓ matched' : r.status === 'ambiguous' ? '⚠ ' + r.candidates.length : r.status === 'no_tracking' ? 'no tracking' : '✗ no match'}</span></td>
                        <td>{r.candidates.length
                          ? <select value={picks[i] || ''} onChange={e => setPicks(p => ({ ...p, [i]: e.target.value }))} style={{ fontSize: 11, padding: 4, maxWidth: 300, background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 6 }}>
                              <option value="">— skip —</option>
                              {r.candidates.map(c => <option key={c.id} value={c.id}>{c.order_number} · {c.channel.replace('ebay_', '')} · {(c.title || c.sku || '').slice(0, 26)}</option>)}
                            </select>
                          : <span style={{ fontSize: 11, color: 'var(--muted)' }}>—</span>}</td>
                      </tr>
                    ))}</tbody></table>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                  <button className="btn btn-primary" disabled={applying || !Object.values(picks).some(Boolean)} onClick={doApply}>
                    {applying ? 'Pushing…' : `Apply & Push (${Object.values(picks).filter(Boolean).length})`}
                  </button>
                </div>
              </>
            )}
            {applyRes && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Results</div>
                <div className="table-wrap"><table><thead><tr><th>Order</th><th>Channel</th><th>Result</th></tr></thead>
                  <tbody>{applyRes.map((r, i) => (
                    <tr key={i}><td className="mono" style={{ fontSize: 11 }}>{r.order_number}</td><td style={{ fontSize: 11 }}>{r.channel}</td>
                      <td style={{ fontSize: 11, color: r.ok ? 'var(--accent2)' : 'var(--danger)' }}>{r.action}{r.msg ? ': ' + r.msg : ''}</td></tr>
                  ))}</tbody></table></div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                  <button className="btn btn-ghost" onClick={() => setShowUpload(false)}>Done</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
